import { Fragment, useEffect, useState } from 'react';
import {
  claveDinamica, kpiDinamicaDelDia, listCanjesDelDiaTodos,
  type CanjeRow, type KpiDelDia,
} from '../../api/canjes';
import { listCierresDeTurno, type CierreEnBandeja } from '../../api/impulso';
import { getPhotoUrl } from '../../api/attendance';
import { formatInTimeZone } from 'date-fns-tz';
import { getDayBoard } from '../../api/positions';
import type { DayBoardRow } from '../../api/positions';
import { listActiveDocuments } from '../../api/documents';
import { documentAlerts } from '../../domain/documents';
import type { DocumentAlert } from '../../domain/documents';
import { dayStatus } from '../../domain/dayStatus';
import type { DayStatus } from '../../domain/dayStatus';
import { loadSettings } from '../../lib/settings';
import { fechaLarga } from '../../domain/planningBoardView';
import FilaTarjeta from '../../components/FilaTarjeta';
import {
  PanelDeDetalle, Seccion, Dato, Senal, BotonDetalle, FILA_ABIERTA,
} from '../../components/DetalleExpandible';

const TZ = 'America/Lima';
const REFRESH_MS = 60_000;

const STATUS_BADGE: Record<DayStatus, { label: string; cls: string }> = {
  sin_asignar: { label: 'Sin asignar', cls: 'bg-gray-200 text-gray-800' },
  esperando: { label: 'Esperando', cls: 'bg-blue-100 text-blue-800' },
  ingresado: { label: 'Ingresó', cls: 'bg-amber-100 text-amber-800' },
  completo: { label: 'Completo', cls: 'bg-green-100 text-green-800' },
  falta: { label: 'Falta', cls: 'bg-red-100 text-red-800' },
};

// Se importa de domain/flags: cada copia local se quedaba sin las banderas nuevas y la
// pantalla mostraba el codigo crudo ('salida_temprana') en vez del texto en espanol.
import { FLAG_LABEL } from '../../domain/flags';
import TarjetasResumen from '../../components/TarjetasResumen';
import { useAuth } from '../../components/AuthProvider';
import { soloSupervisaTiendas } from '../../domain/permissions';

interface BoardItem {
  id: string;
  pdvId: string | null;
  pdv: string;
  district: string | null;
  campaign: string;
  /** El id, no el rótulo: es la mitad de la clave con la que se busca la dinámica (0095). */
  campaignId: string | null;
  schedule: string;
  worker: string | null;
  /** Para cruzar el turno con lo que esa persona registró, sin comparar nombres. */
  workerId: string | null;
  estado: DayStatus;
  flags: string[];
  /** Las marcas del turno, con su hora. Es el corazón del detalle: qué pasó y cuándo. */
  marcas: { kind: 'ingreso' | 'salida'; hora: string | null; flags: string[] }[];
}

function toItems(rows: DayBoardRow[], nowLima: string): BoardItem[] {
  return rows.map((p) => {
    const assignment = p.assignments.find((a) => a.status === 'aprobada') ?? null;
    const marks = assignment?.attendance ?? [];
    const hasEntry = marks.some((m) => m.kind === 'ingreso');
    const hasExit = marks.some((m) => m.kind === 'salida');
    return {
      id: p.id,
      pdvId: p.pdv?.id ?? null,
      pdv: p.pdv?.name ?? '—',
      district: p.pdv?.district ?? null,
      campaign: p.campaign ? `${p.campaign.client} — ${p.campaign.brand}` : '—',
      campaignId: p.campaign?.id ?? null,
      schedule: `${p.start_time.slice(0, 5)}–${p.end_time.slice(0, 5)}`,
      worker: assignment?.worker
        ? `${assignment.worker.first_names} ${assignment.worker.last_names}`
        : null,
      workerId: assignment?.worker?.id ?? null,
      marcas: marks.map((m) => ({
        kind: m.kind,
        // `device_ts` es la hora que declaró el celular al marcar; si falta, la de llegada al
        // servidor. Mostrar una u otra es preferible a no mostrar hora: el coordinador lee este
        // detalle para saber a qué hora entró alguien, no para auditar el reloj del teléfono.
        hora: m.device_ts ?? m.created_at ?? null,
        flags: m.flags ?? [],
      })),
      estado: dayStatus({
        hasApprovedAssignment: assignment !== null,
        hasEntry,
        hasExit,
        date: p.date,
        endTime: p.end_time,
        nowLima,
      }),
      flags: [...new Set(marks.flatMap((m) => m.flags ?? []))],
    };
  });
}

/** Miniatura de la boleta en el detalle de canjes. La firma dura 60 s: se pide por fila. */
function MiniaturaCanje({ path }: { path: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [falla, setFalla] = useState(false);
  useEffect(() => {
    if (path === null) return;
    let vivo = true;
    void getPhotoUrl(path).then((u) => { if (vivo) setUrl(u); }).catch(() => { if (vivo) setFalla(true); });
    return () => { vivo = false; };
  }, [path]);
  // Sin foto NO es lo mismo que foto rota: desde la 0036 la boleta es opcional porque hay tiendas
  // que no dejan usar el celular. Al coordinador le sirve distinguir "se registró sin evidencia"
  // de "la evidencia existe y no se pudo cargar".
  if (path === null) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-dashed bg-gray-50 text-center text-[10px] leading-tight text-gray-500">
        sin foto
      </div>
    );
  }
  if (falla) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border bg-gray-50 text-[10px] text-gray-500">
        sin vista
      </div>
    );
  }
  if (!url) return <div className="h-12 w-12 shrink-0 animate-pulse rounded border bg-gray-100" />;
  return <img src={url} alt="Boleta" className="h-12 w-12 shrink-0 rounded border object-cover" />;
}

/**
 * El detalle de UN turno, desplegado bajo su fila.
 *
 * Reemplaza a la columna «Dinámica», que era un número fijo para todas las campañas y por eso no
 * podía decir la verdad: pintaba «0» en Bimbo, que no tiene canjes sino cierre de turno. Acá sale
 * lo que ESA campaña tenga y nada más; si no hay nada registrado, se dice con todas sus letras en
 * lugar de mostrar un cero que se lee como negligencia de la impulsadora.
 *
 * Los canjes no se listan acá: se ofrece abrirlos en el detalle que ya existe, con sus boletas y su
 * miniatura. Duplicar esa tabla adentro de una fila sería mantener dos veces la misma pantalla.
 */
function DetalleDelTurno({ item, canjes, kpi, cierre, onVerCanjes }: {
  item: BoardItem;
  canjes: CanjeRow[];
  kpi: KpiDelDia | undefined;
  cierre: CierreEnBandeja | undefined;
  onVerCanjes: (titulo: string, filas: CanjeRow[]) => void;
}) {
  const hora = (iso: string | null): string =>
    iso === null ? '—' : formatInTimeZone(new Date(iso), TZ, 'HH:mm');
  const ingreso = item.marcas.find((m) => m.kind === 'ingreso');
  const salida = item.marcas.find((m) => m.kind === 'salida');
  const hayAlgo = item.marcas.length > 0 || kpi !== undefined || cierre !== undefined;
  // Lo que quedó en cero no se guarda (ver `CierreEnBandeja.porSku`), pero por si acaso se filtra:
  // una fila «Rap 00: 0» ocupa el mismo renglón que una venta real y no dice nada.
  const porSku = cierre?.porSku.filter((x) => x.unidades > 0) ?? [];
  const haySenales = cierre !== undefined && (
    cierre.registroTardio || cierre.sinAsistencia
    || cierre.quiebre !== 'ninguno' || cierre.incidencia !== 'ninguna'
    || cierre.objeciones.length > 0
  );

  return (
    <>
      <Seccion titulo="Asistencia">
        {item.marcas.length === 0 ? (
          <p className="text-sm text-gray-600">
            {item.worker === null
              ? 'La posición no tiene a nadie asignado.'
              : 'Todavía no marcó ingreso.'}
          </p>
        ) : (
          // Ingreso y salida en dos columnas y no en dos renglones «Ingreso: 08:12»: son el mismo
          // tipo de dato dos veces, y puestos en paralelo se comparan de un vistazo.
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
            {([['Ingreso', ingreso], ['Salida', salida]] as const).map(([rotulo, marca]) => (
              <Dato key={rotulo} etiqueta={rotulo}>
                {marca === undefined ? (
                  // gray-400 no llega a 4.5:1 sobre blanco, y «sin marcar» es justo el dato que
                  // no se puede perder de vista.
                  <span className="text-gray-500">sin marcar</span>
                ) : (
                  <span className="flex flex-wrap items-center gap-1">
                    <span className="font-medium">{hora(marca.hora)}</span>
                    {marca.flags.map((f) => (
                      <Senal key={f} tono="grave">{FLAG_LABEL[f] ?? f}</Senal>
                    ))}
                  </span>
                )}
              </Dato>
            ))}
          </dl>
        )}
      </Seccion>

      {/* Solo si en esa tienda corre una dinámica de canje. Sin `kpi` no hay nada que contar y el
          bloque no aparece: es la diferencia entre «no aplica» y «aplica y va en cero». */}
      {kpi !== undefined && (
        <Seccion
          titulo={kpi.label}
          // La meta vive en el encabezado y no dentro del texto: así está en los dos casos —con
          // registros y sin ellos— y siempre en el mismo sitio.
          extra={kpi.metaDia !== null
            ? <span className="text-xs text-gray-600">meta del día {kpi.metaDia}</span>
            : undefined}
        >
          {canjes.length === 0 ? (
            <p className="text-sm text-amber-700">Sin registros todavía</p>
          ) : (
            <button
              type="button"
              onClick={() => onVerCanjes(kpi.label, canjes)}
              className="flex items-baseline gap-2 text-left"
            >
              <span className="text-lg font-semibold text-gray-900">{canjes.length}</span>
              <span className="text-sm text-indigo-700 underline">ver el detalle</span>
            </button>
          )}
        </Seccion>
      )}

      {cierre !== undefined && (
        <Seccion
          titulo="Cierre de turno"
          extra={<span className="text-sm font-semibold text-gray-900">{cierre.unidades} unidades</span>}
        >
          <div className="space-y-3">
            {porSku.length === 0 ? (
              <p className="text-sm text-gray-600">Sin desglose por producto.</p>
            ) : (
              // Antes esto era un paréntesis con todo pegado —«(rap_00: 3 · tos_clasicas: 5)»— y
              // el usuario no lo podía leer: *«se ve feo, abreviado y todo en una fila»*. Un
              // renglón por producto, con la cifra alineada a la derecha, se recorre en vertical
              // y deja comparar cantidades sin leer palabra por palabra. Sin caja ni borde
              // alrededor: la lista ya está dentro del panel y encerrarla otra vez la anida.
              <ul className="divide-y divide-gray-100">
                {porSku.map((x) => (
                  <li key={x.sku} className="flex items-baseline justify-between gap-4 py-1.5">
                    <span className="min-w-0 break-words text-sm text-gray-700">{x.sku}</span>
                    <span className="shrink-0 text-sm font-medium tabular-nums text-gray-900">
                      {x.unidades}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/* El contenedor se monta solo si hay alguna señal: un div vacío igual se lleva el
                espacio del `space-y-3` y abre un hueco que se lee como algo que no cargó. */}
            {haySenales && (
              <div className="flex flex-wrap gap-1">
                {cierre.registroTardio && <Senal>tardío</Senal>}
                {cierre.sinAsistencia && <Senal>sin marca</Senal>}
                {cierre.quiebre !== 'ninguno' && <Senal>quiebre</Senal>}
                {cierre.incidencia !== 'ninguna' && <Senal>incidencia</Senal>}
                {cierre.objeciones.length > 0 && (
                  <Senal>{cierre.objeciones.length} objeciones</Senal>
                )}
              </div>
            )}
            {cierre.incidenciaDetalle !== null && (
              // El panel ahora es blanco: un `bg-white` acá era invisible. El gris apenas marca
              // que es texto que escribió la impulsadora y no un dato del sistema.
              <p className="rounded bg-gray-50 px-2 py-1.5 text-sm text-gray-700">
                {cierre.incidenciaDetalle}
              </p>
            )}
            <p className="text-xs text-gray-500">
              El cierre completo está en Registros de campo → Cierres de turno.
            </p>
          </div>
        </Seccion>
      )}

      {!hayAlgo && (
        <p className="text-sm text-gray-600">
          Este turno todavía no tiene nada registrado, y su campaña no lleva ninguna dinámica.
        </p>
      )}
    </>
  );
}

export default function Hoy() {
  const { roles } = useAuth();
  /**
   * El supervisor de ruta ve el tablero de SUS tiendas (0218). La tarjeta de documentos no va: la RLS
   * de `worker_documents` le deja ver solo los suyos, y la tarjeta los contaría como si fueran del
   * equipo.
   */
  const soloTiendas = soloSupervisaTiendas(roles);
  const [items, setItems] = useState<BoardItem[]>([]);
  /** Solo para la tarjeta del conteo: la lista se lee en «Trabajadores». */
  const [alerts, setAlerts] = useState<DocumentAlert[]>([]);
  const [updatedAt, setUpdatedAt] = useState('');
  /**
   * El día que se está mirando. Arranca en hoy y se puede mover con el selector.
   *
   * Es la misma vista para cualquier fecha —no una pantalla aparte de "histórico"—: el coordinador
   * ya sabe leer este tablero, y para revisar el sábado pasado no debería tener que aprender otro.
   */
  const [fecha, setFecha] = useState(() => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd'));
  const [error, setError] = useState('');
  /**
   * Campaña elegida, o `''` para todas. Filtra en el navegador y NO en la consulta: el tablero
   * del día ya trae todas las posiciones de la fecha, y volver a la base por cada cambio de
   * campaña sería un viaje para descartar filas que ya están en memoria.
   */
  const [campania, setCampania] = useState('');
  /**
   * Canjes de hoy agrupados por **campaña + tienda** (`claveDinamica`), no por tienda.
   *
   * Dos campañas comparten local el mismo día —Bimbo y Makro en Santa Anita— y agrupando solo por
   * tienda, los canjes de una se le atribuían a la otra.
   */
  const [canjesPorDinamica, setCanjesPorDinamica] = useState<Map<string, CanjeRow[]>>(new Map());
  /** Indicador que aplica a cada campaña+tienda hoy. Sin entrada = ahí no corre ninguna dinámica. */
  const [kpiPorDinamica, setKpiPorDinamica] = useState<Map<string, KpiDelDia>>(new Map());
  const [detalle, setDetalle] = useState<{ pdv: string; titulo: string; filas: CanjeRow[] } | null>(null);
  /**
   * Cierres de turno del día, por trabajador. Es la otra dinámica: Bimbo no tiene canjes y sí
   * tiene cierre de turno, así que sin esto el detalle de una fila de Bimbo estaría vacío
   * precisamente donde hay algo que mirar.
   */
  const [cierresPorWorker, setCierresPorWorker] = useState<Map<string, CierreEnBandeja>>(new Map());
  /** Qué fila está desplegada. Una a la vez: abrir varias convierte la tabla en un muro. */
  const [abierta, setAbierta] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    async function cargar() {
      try {
        const nowLima = formatInTimeZone(new Date(), TZ, "yyyy-MM-dd'T'HH:mm:ss");
        const hoy = fecha;
        const [rows, docs, settings, canjes, kpis, cierres] = await Promise.all([
          getDayBoard(hoy),
          soloTiendas ? Promise.resolve([]) : listActiveDocuments(),
          loadSettings(),
          // Los canjes NO pueden tumbar el tablero del día: es la pantalla con la que se
          // gobierna la operación, y una campaña sin dinámica no tiene por qué romperla.
          listCanjesDelDiaTodos(hoy).catch(() => [] as CanjeRow[]),
          kpiDinamicaDelDia(hoy),
          // Mismo trato que los canjes, y por lo mismo.
          listCierresDeTurno(hoy, hoy).catch(() => [] as CierreEnBandeja[]),
        ]);
        if (!alive) return;
        setItems(toItems(rows, nowLima));
        const porDinamica = new Map<string, CanjeRow[]>();
        for (const c of canjes) {
          if (!c.pdv?.id) continue;
          const k = claveDinamica(c.campaignId, c.pdv.id);
          porDinamica.set(k, [...(porDinamica.get(k) ?? []), c]);
        }
        setCanjesPorDinamica(porDinamica);
        // Se pregunta por la VIGENCIA, no se deduce de si hubo registros: una tienda con
        // dinámica que aún no registró nada tiene que verse como "0", no como "no aplica".
        // Las que ya registraron se suman por si la vigencia se acotó después de registrar.
        const mapa = new Map(kpis);
        for (const [k, filas] of porDinamica) {
          const c = filas[0];
          if (!mapa.has(k) && c.pdv?.id) {
            mapa.set(k, { campaignId: c.campaignId, pdvId: c.pdv.id, label: 'Canjes', metaDia: null });
          }
        }
        setKpiPorDinamica(mapa);
        setCierresPorWorker(new Map(
          cierres.filter((c) => c.workerId !== null).map((c) => [c.workerId as string, c]),
        ));
        setAlerts(documentAlerts(docs, hoy, settings.docWarnDays));
        setUpdatedAt(nowLima.slice(11, 16));
        setError('');
      } catch (e) {
        if (alive) setError('Error al cargar el tablero: ' + (e instanceof Error ? e.message : String(e)));
      }
    }

    void cargar();
    // El refresco automático solo tiene sentido mirando HOY: un día cerrado ya no cambia, y
    // recargarlo cada minuto solo gasta consultas y hace parpadear una pantalla que se está
    // leyendo con calma.
    const esHoy = fecha === formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
    const timer = esHoy ? setInterval(cargar, REFRESH_MS) : null;
    return () => {
      alive = false;
      if (timer !== null) clearInterval(timer);
    };
  }, [fecha, soloTiendas]);

  /**
   * Las campañas que de verdad tienen posiciones ese día. Se saca de lo cargado y no de la tabla
   * `campaigns`: ofrecer una campaña que ese día no activa ninguna tienda solo consigue que el
   * filtro devuelva una pantalla vacía y parezca roto.
   */
  const campanias = [...new Set(items.map((i) => i.campaign))].sort();

  /**
   * Lo que se ve. Los contadores de abajo cuentan SOBRE ESTO, no sobre el total: un tablero que
   * dice «19 posiciones» mientras muestra 8 obliga a desconfiar de los dos números.
   */
  const visibles = campania === '' ? items : items.filter((i) => i.campaign === campania);

  const cubiertas = visibles.filter((i) => i.estado !== 'sin_asignar').length;
  const conIngreso = visibles.filter(
    (i) => i.estado === 'ingresado' || i.estado === 'completo',
  ).length;
  const sinCubrir = visibles.filter((i) => i.estado === 'sin_asignar').length;
  const faltas = visibles.filter((i) => i.estado === 'falta').length;
  const alertasFlags = visibles.reduce((s, i) => s + i.flags.length, 0);
  /**
   * "Hoy" solo no dice qué día es, y este tablero se mira, se comenta y se captura para mandarlo
   * por WhatsApp: fuera de la pantalla, un pantallazo sin fecha no se puede fechar después.
   *
   * Se arma con `fechaLarga`, la misma de Planificación: calcula el día de semana con aritmética
   * sobre la cadena (Sakamoto), sin `Date` ni configuración regional del navegador. Un `Date` acá
   * podría correr el nombre del día en un navegador con otra zona, que es justo lo que se evitó
   * en el tablero del mes.
   */
  const hoyLargo = fecha === '' ? '' : fechaLarga(fecha);
  const hoyReal = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const viendoHoy = fecha === hoyReal;

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold">
          {viendoHoy ? 'Hoy' : 'Día'}
          {hoyLargo && <span className="font-normal text-gray-600">, {hoyLargo}</span>}
        </h1>
        <div className="flex items-center gap-3">
          {/* Mismo tablero, otro día. El botón "Hoy" existe porque después de revisar el sábado
              pasado lo que se quiere es volver a la operación viva, y buscar la fecha de hoy en
              el calendario para volver es un paso de más. */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Ver el día
            <input
              type="date"
              value={fecha}
              max={hoyReal}
              onChange={(e) => setFecha(e.target.value || hoyReal)}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          {/* Solo aparece si ese día hay más de una campaña: un desplegable de una sola opción
              es ruido. */}
          {campanias.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Campaña
              <select
                value={campania}
                onChange={(e) => setCampania(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="">Todas ({items.length})</option>
                {campanias.map((c) => (
                  <option key={c} value={c}>
                    {c} ({items.filter((i) => i.campaign === c).length})
                  </option>
                ))}
              </select>
            </label>
          )}
          {!viendoHoy && (
            <button
              type="button"
              onClick={() => setFecha(hoyReal)}
              className="rounded border border-blue-600 px-2 py-1 text-sm text-blue-700"
            >
              Volver a hoy
            </button>
          )}
          <p className="text-xs text-gray-500">
            {!updatedAt
              ? 'Cargando…'
              : viendoHoy
                ? `Actualizado ${updatedAt} · se refresca cada minuto`
                : 'Día cerrado · no se refresca'}
          </p>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {/* Las mismas seis tarjetas de antes, ahora con el componente compartido: el tono pinta el
          número solo cuando NO es cero, así que un día sin faltantes ni alertas se ve tranquilo y
          el rojo que aparece es rojo de verdad. */}
      <TarjetasResumen className="mb-6" indicadores={[
        { titulo: 'Posiciones de hoy', valor: visibles.length },
        { titulo: 'Cubiertas', valor: cubiertas, detalle: `de ${visibles.length}` },
        { titulo: 'Con ingreso', valor: conIngreso },
        { titulo: 'Faltantes', valor: sinCubrir + faltas, tono: 'malo',
          detalle: `sin cubrir ${sinCubrir} · sin marcar ${faltas}` },
        { titulo: 'Alertas', valor: alertasFlags, tono: 'atencion' },
        ...(soloTiendas ? [] : [{ titulo: 'Documentos por vencer', valor: alerts.length, tono: 'atencion' as const,
          detalle: `${alerts.filter((a) => a.severity === 'vencido').length} vencidos` }]),
      ]} />

      {/* La LISTA de documentos por vencer se movió a «Trabajadores» —pedido del usuario— y vive en
          `components/AlertasDeDocumentos.tsx`. Acá queda solo la tarjeta con el conteo, que sí es
          del tablero del día: avisa que hay algo que atender sin pretender que se resuelva desde
          esta pantalla. Un carnet que vence en nueve días no se arregla hoy; se arregla llamando a
          la persona, y eso se hace desde Trabajadores. */}

      {/* En celular: una tarjeta por posición (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {visibles.map((i) => (
          <FilaTarjeta
            key={i.id}
            titulo={i.pdv}
            subtitulo={i.district}
            estados={
              <>
                <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_BADGE[i.estado].cls}`}>
                  {STATUS_BADGE[i.estado].label}
                </span>
                {i.flags.map((f) => (
                  <span key={f} className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                    {FLAG_LABEL[f] ?? f}
                  </span>
                ))}
              </>
            }
            campos={[
              { etiqueta: 'Trabajador', valor: i.worker },
              { etiqueta: 'Campaña', valor: i.campaign },
              { etiqueta: 'Horario', valor: i.schedule },
            ]}
          />
        ))}
        {visibles.length === 0 && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">
            No hay posiciones programadas para hoy.
          </p>
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full border text-sm">
          {/* `bg-gray-50` como las otras dos bandejas: el encabezado de tabla es el mismo concepto
              en las tres pantallas y tenía tres grises distintos. */}
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">PDV</th>
              <th className="p-2 text-left">Campaña</th>
              <th className="p-2">Horario</th>
              <th className="p-2 text-left">Trabajador</th>
              <th className="p-2">Estado</th>
              <th className="p-2 text-left">Alertas</th>
              {/*
                Antes acá había una columna «Dinámica» con el contador de canjes. Se quitó el
                2026-09-05 por pedido del usuario, y el motivo es que MENTÍA: pintaba «0» en las 20
                tiendas de Bimbo, que no tiene canjes —su dinámica es el cierre de turno—, y ese
                cero se lee como «no registró nada» cuando lo cierto es «acá no aplica».

                El reemplazo no es otra columna con otro número: es abrir el turno. Cada campaña
                registra cosas distintas, así que una sola columna nunca va a poder nombrarlas
                todas. Adentro sale lo que ESA campaña tenga, y si no tiene nada, se dice.
              */}
              <th className="p-2">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((i) => {
              // La dinámica se busca por campaña Y tienda. Con la tienda sola, una fila de Bimbo en
              // Makro Santa Anita mostraba la «Raspa y Gana» de la campaña de Makro (0095).
              const clave = i.campaignId && i.pdvId ? claveDinamica(i.campaignId, i.pdvId) : null;
              return (
              <Fragment key={i.id}>
                <tr className={`border-t ${abierta === i.id ? FILA_ABIERTA : ''}`}>
                <td className="p-2">
                  {i.pdv}
                  {i.district && <span className="text-xs text-gray-500"> · {i.district}</span>}
                </td>
                <td className="p-2">{i.campaign}</td>
                <td className="p-2 text-center">{i.schedule}</td>
                <td className="p-2">{i.worker ?? <span className="text-gray-400">— sin asignar —</span>}</td>
                <td className="p-2 text-center">
                  <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_BADGE[i.estado].cls}`}>
                    {STATUS_BADGE[i.estado].label}
                  </span>
                </td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {i.flags.map((f) => (
                      <span key={f} className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                        {FLAG_LABEL[f] ?? f}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-2 text-center">
                  <BotonDetalle
                    abierto={abierta === i.id}
                    onClick={() => setAbierta(abierta === i.id ? null : i.id)}
                  />
                </td>
                </tr>
                {abierta === i.id && (
                  // La celda no lleva relleno: el canal gris y la sangría son del panel, y un
                  // `p-3` acá los empujaría hacia adentro dejando un marco de fondo de tabla.
                  <tr>
                    <td colSpan={7} className="p-0">
                      <PanelDeDetalle
                        titulo="Detalle del turno"
                        // Tienda, persona y horario: es lo que amarra el panel a la fila que se
                        // abrió, para quien lo mira después de bajar y ya no ve esa fila.
                        sujeto={`${i.pdv} · ${i.worker ?? 'sin asignar'} · ${i.schedule}`}
                        onCerrar={() => setAbierta(null)}
                      >
                        <DetalleDelTurno
                          item={i}
                          canjes={clave === null ? [] : (canjesPorDinamica.get(clave) ?? [])}
                          kpi={clave === null ? undefined : kpiPorDinamica.get(clave)}
                          cierre={i.workerId ? cierresPorWorker.get(i.workerId) : undefined}
                          onVerCanjes={(titulo, filas) => setDetalle({ pdv: i.pdv, titulo, filas })}
                        />
                      </PanelDeDetalle>
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}
            {visibles.length === 0 && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  No hay posiciones programadas para hoy.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detalle && (
        // La capa de modal es `z-50` en TODA la app. Acá era `z-20` mientras la bandeja de canjes
        // usaba `z-50` para exactamente el mismo rol —overlay a pantalla completa—, y dos números
        // distintos para el mismo concepto es cómo se termina con un modal debajo de otra cosa.
        // El alto máximo se unifica igual: `max-h-[90vh]`, el de la bandeja.
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded bg-white p-4">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <h3 className="font-semibold">
                {detalle.titulo} {viendoHoy ? 'de hoy' : `del ${fecha.slice(8, 10)}/${fecha.slice(5, 7)}`} · {detalle.pdv}
              </h3>
              <button className="text-sm underline" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
            <ul className="space-y-2">
              {detalle.filas.map((c) => (
                <li key={c.id} className="flex gap-3 rounded border p-2 text-sm">
                  <MiniaturaCanje path={c.fotoBoletaPath} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {c.lineas.flatMap((l) => l.premios.map((p) => p.prizeName)).join(' + ') || 'Sin premio'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {c.worker ? `${c.worker.firstNames} ${c.worker.lastNames}` : 'Recurso no visible'}
                      {' · '}
                      {formatInTimeZone(new Date(c.registradoEn), TZ, 'HH:mm')}
                    </p>
                    {c.marcasDejadas.length > 0 && (
                      <p className="text-xs text-gray-500">Dejó: {c.marcasDejadas.join(', ')}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
